import { useState } from 'react';

import { Box, Paper, Typography } from '@mui/material';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import FullScreenLoader from 'src/components/FullscreenLoading';
import { processRunFidcBySpreedSheet } from 'src/services/run-fidc';
import { ScrollToTop } from 'src/utils/scroll';

import { AlertDialog, type AlertSeverity } from './AlertDialog';
import { HistoryDialog } from './History';
import { SheetsCollector } from './SheetsCollector';
import { Totalizations } from './Totalizations';
import {
  ImportFormatError,
  type MRTTableConfig,
  parseDataBySeparatorType,
  parseTableDataToLinesAndSeparator,
  ProcessRunFidcResponseType,
  setColumnsByData,
} from './utils';

export default function RunFIDC() {
  const [totalizationsOpen, setTotalizationsOpen] = useState(false);
  const [tableConfig, setTableConfig] = useState<MRTTableConfig | null>(null);
  const [totalizerAmounts, setTotalizerAmounts] = useState<{
    processedAmount: number;
    foundAmount: number;
    notFoundAmount: number;
  }>({ processedAmount: 0, foundAmount: 0, notFoundAmount: 0 });

  const [alertDialog, setAlertDialog] = useState<{
    open: boolean;
    message: string;
    severity: AlertSeverity;
    title?: string;
  }>({ open: false, message: '', severity: 'error' });

  const [openHistoryDialog, setOpenHistoryDialog] = useState<boolean>(false);

  const { mutate: processRunFidcMutation, isLoading: isLoadingProcessRunFidc } =
    useMutation({
      mutationFn: (tableData: string) => processRunFidcBySpreedSheet(tableData),
      onSuccess: ({
        data,
        processedAmount,
        notFoundAmount,
        foundAmount,
      }: ProcessRunFidcResponseType) => {
        toast.success('Tarefa finalizada com sucesso.');
        const { lines, separator } = parseTableDataToLinesAndSeparator(
          String(data),
        );
        const parsed = parseDataBySeparatorType(lines, separator);
        const config = setColumnsByData(parsed);
        setTableConfig(config);
        setTotalizerAmounts({
          processedAmount,
          foundAmount,
          notFoundAmount,
        });
        setTotalizationsOpen(true);
      },
      onError: (error: Error) => {
        onErrorGenerateUrls(error);
      },
    });

  const handleGenerateUrls = (tableData: string) => {
    try {
      processRunFidcMutation(tableData);
    } catch (e) {
      if (e instanceof ImportFormatError) {
        toast.error(e.message);
      } else {
        toast.error('Ocorreu um erro ao gerar as URLs. Tente novamente.');
      }
    }
  };

  const onErrorGenerateUrls = (error: Error) => {
    const message =
      error?.message ?? 'Ocorreu um erro ao gerar as URLs. Tente novamente.';
    setAlertDialog({ open: true, message, severity: 'error' });
  };

  const closeAlertDialog = () => {
    setAlertDialog((prev) => ({ ...prev, open: false }));
  };

  return (
    <ScrollToTop>
      <Box component="section" sx={{ width: '100%', gap: 2 }}>
        <Paper elevation={2}>
          <Box p={2} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="h6">
              Run FIDC - Gerador automático de URLs
            </Typography>

            <SheetsCollector
              onSubmit={handleGenerateUrls}
              isLoading={isLoadingProcessRunFidc}
              onOpenHistoryDialog={() => setOpenHistoryDialog(true)}
            />
          </Box>
          <Totalizations
            open={totalizationsOpen}
            onClose={() => setTotalizationsOpen(false)}
            isLoading={false}
            tableConfig={tableConfig}
            processed={totalizerAmounts.processedAmount}
            found={totalizerAmounts.foundAmount}
            notFounds={totalizerAmounts.notFoundAmount}
          />

          <AlertDialog
            open={alertDialog.open}
            onClose={closeAlertDialog}
            message={alertDialog.message}
            severity={alertDialog.severity}
            title={alertDialog.title}
          />

          <HistoryDialog
            open={openHistoryDialog}
            onClose={() => setOpenHistoryDialog(false)}
          />
        </Paper>
      </Box>
      <FullScreenLoader open={isLoadingProcessRunFidc} />
    </ScrollToTop>
  );
}
